import {
  addComponent,
  compileMap,
  createMap,
  rectOf,
  type MapDocument,
} from '../builder/MapDocument';
import type { LevelDefinition } from '../world/LevelDefinition';

/**
 * Component Gallery: the one deck in the catalog that is **authored with the map
 * builder** rather than written out by hand.
 *
 * The document below is exactly what the 2D editor edits and the 3D builder
 * furnishes: five components from the library (both hallway orientations, a
 * room, a junction and a bay), doorways generated wherever they touch, and cover
 * and drones placed the way the scanner places them — including a crate stacked
 * on a crate and drones anchored to three different faces. `compileMap` turns it
 * into the level, so the deck you play is the builder's output, and the document
 * is what you would reopen to change it.
 */
function buildDocument(): MapDocument {
  const map = createMap('Component Gallery');
  map.difficulty = 'standard';
  map.seed = 4711;

  // Five cells of layout: a 2-cell hall west, a room, a junction below it, a bay
  // east and a rotated hall running along the south. Every pair that touches
  // gets a doorway from `compileMap`; there are no hand-authored portals here.
  const hallWest = addComponent(map, 'hall', [0, 0]);
  const room = addComponent(map, 'room', [2, 0]);
  const junction = addComponent(map, 'junction', [2, 4]);
  const bay = addComponent(map, 'bay', [8, 0]);
  const hallSouth = addComponent(map, 'hall', [4, 8], 90);

  const rects = {
    hallWest: rectOf(hallWest),
    room: rectOf(room),
    junction: rectOf(junction),
    bay: rectOf(bay),
    hallSouth: rectOf(hallSouth),
  };

  // Cover, in the palette's own sizes. The two crates in the room are the
  // scanner's stack: the second sits exactly on the first.
  map.assets.push(
    {
      kind: 'crate',
      position: [rects.hallWest.centreX, 0.6, rects.hallWest.minZ + 3],
      size: [1.2, 1.2, 1.2],
    },
    {
      kind: 'barrier',
      position: [rects.room.minX + 6, 0.7, rects.room.centreZ],
      size: [4, 1.4, 0.4],
    },
    {
      kind: 'crate',
      position: [rects.room.minX + 2, 0.6, rects.room.maxZ - 2],
      size: [1.2, 1.2, 1.2],
    },
    {
      kind: 'crate',
      position: [rects.room.minX + 2, 1.8, rects.room.maxZ - 2],
      size: [1.2, 1.2, 1.2],
    },
    // This console and the junction's pillar both sit clear of a doorway: cover
    // standing in an opening reads as a broken deck, and the builder warns
    // about it (`mapWarnings`), so the deck that ships must be clean of it.
    {
      kind: 'console',
      position: [rects.room.maxX - 4, 0.5, rects.room.centreZ],
      size: [2.2, 1, 0.9],
    },
    {
      kind: 'pillar',
      position: [rects.junction.centreX, 2, rects.junction.centreZ],
      size: [1.1, 4, 1.1],
    },
    {
      kind: 'console',
      position: [rects.junction.centreX + 3, 0.5, rects.junction.maxZ - 2],
      size: [2.2, 1, 0.9],
    },
    { kind: 'crate', position: [rects.bay.centreX, 0.6, rects.bay.centreZ], size: [1.2, 1.2, 1.2] },
    {
      kind: 'console',
      position: [rects.hallSouth.centreX + 4, 0.5, rects.hallSouth.centreZ],
      size: [2.2, 1, 0.9],
    },
    {
      kind: 'crate',
      position: [rects.hallSouth.minX + 4, 0.6, rects.hallSouth.centreZ],
      size: [1.2, 1.2, 1.2],
    },
  );

  // Drones, anchored the way the scanner anchors them: the raw point on the face
  // it is placed against, with the hover height left to the drone.
  map.drones.push(
    {
      position: [rects.hallWest.centreX, 0, rects.hallWest.centreZ + 4],
      surface: 'floor',
      magnetic: false,
    },
    {
      position: [rects.room.centreX + 2, 0, rects.room.maxZ - 2],
      surface: 'floor',
      magnetic: true,
    },
    {
      position: [rects.room.centreX + 2, rects.room.height, rects.room.minZ + 2],
      surface: 'ceiling',
      magnetic: false,
    },
    {
      position: [rects.room.minX + 0.3, 2.4, rects.room.centreZ + 1],
      surface: 'left',
      magnetic: false,
    },
    {
      position: [rects.junction.centreX + 2, 0, rects.junction.centreZ],
      surface: 'floor',
      magnetic: false,
    },
    {
      position: [rects.junction.maxX - 0.3, 2.4, rects.junction.minZ + 2],
      surface: 'right',
      magnetic: true,
    },
    {
      position: [rects.bay.centreX + 2, 0, rects.bay.centreZ + 2],
      surface: 'floor',
      magnetic: false,
    },
    {
      position: [rects.hallSouth.centreX, rects.hallSouth.height, rects.hallSouth.centreZ],
      surface: 'ceiling',
      magnetic: false,
    },
  );

  return map;
}

/** The builder document behind the level, kept so a test can re-compile it. */
export const COMPONENT_GALLERY_MAP: MapDocument = buildDocument();

/** The deck the game runs: the builder's own output. */
export const COMPONENT_GALLERY: LevelDefinition = compileMap(COMPONENT_GALLERY_MAP);
